import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  NativeSelect,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconAlertTriangle, IconCircleCheck, IconCopy } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import {
  useCopyDermConfig,
  useDeleteDermConfig,
  useDermConfig,
  useOrgs,
  useTestDermConnection,
  useUpsertDermConfig,
} from '../api/orgs';

interface Props {
  orgId: string;
  archived: boolean;
}

function dermErrorMessage(
  code: 'no_config' | 'unreachable' | 'invalid_credentials' | 'missing_token',
  fallback: string,
): string {
  switch (code) {
    case 'invalid_credentials':
      return 'DERM rejected the credentials. Double-check the username and password.';
    case 'unreachable':
      return 'Could not reach DERM at the configured base URL. Check the URL and the network.';
    case 'missing_token':
      return 'DERM returned no authorization token — the endpoint may be misconfigured.';
    case 'no_config':
      return 'No DERM config exists yet for this organisation.';
    default:
      return fallback;
  }
}

/**
 * "DERM integration" card. Renders on the org detail page. Lets an admin
 * configure the per-org credentials used by the DERM (AI medical device)
 * client. The password is write-only — the server returns `has_password`
 * but never the value, and this form preserves the existing password
 * when the field is left blank on update.
 */
export function DermConfigCard({ orgId, archived }: Props) {
  const query = useDermConfig(orgId);
  const upsert = useUpsertDermConfig(orgId);
  const remove = useDeleteDermConfig(orgId);
  const testConn = useTestDermConnection(orgId);
  const copy = useCopyDermConfig(orgId);
  const orgs = useOrgs();

  const [baseUrl, setBaseUrl] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [feedback, setFeedback] = useState<
    { kind: 'ok'; message: string } | { kind: 'error'; message: string } | null
  >(null);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copySourceOrgId, setCopySourceOrgId] = useState('');

  const existing = query.data ?? null;

  useEffect(() => {
    if (existing) {
      setBaseUrl(existing.base_url);
      setUsername(existing.username);
    }
    // Clear the password input whenever the underlying record changes —
    // we never have the existing plaintext, so leaving the field blank
    // is the correct default on update.
    setPassword('');
  }, [existing?.organisation_id, existing?.updated_at]);

  if (query.isLoading) {
    return (
      <Card withBorder padding="lg" maw={680}>
        <Text c="dimmed">Loading DERM integration…</Text>
      </Card>
    );
  }

  const isCreate = !existing;

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFeedback(null);
    if (!baseUrl.trim() || !username.trim()) {
      setFeedback({ kind: 'error', message: 'Base URL and username are required.' });
      return;
    }
    if (isCreate && !password) {
      setFeedback({ kind: 'error', message: 'Password is required when creating a new config.' });
      return;
    }
    upsert.mutate(
      {
        base_url: baseUrl.trim(),
        username: username.trim(),
        // Empty string means "leave existing password unchanged"; only send
        // the field when the admin typed something.
        ...(password ? { password } : {}),
      },
      {
        onSuccess: () => {
          setPassword('');
          setFeedback({
            kind: 'ok',
            message: isCreate ? 'DERM integration configured.' : 'DERM integration updated.',
          });
        },
        onError: (err) =>
          setFeedback({
            kind: 'error',
            message: err instanceof Error ? err.message : 'Failed to save.',
          }),
      },
    );
  }

  function handleTestConnection() {
    setFeedback(null);
    testConn.mutate(undefined, {
      onSuccess: (result) => {
        if (result.ok) {
          setFeedback({
            kind: 'ok',
            message: 'Connected successfully — DERM accepted the credentials.',
          });
        } else {
          setFeedback({
            kind: 'error',
            message: dermErrorMessage(result.code, result.message),
          });
        }
      },
      onError: (err) =>
        setFeedback({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Test connection failed.',
        }),
    });
  }

  function handleCopy() {
    if (!copySourceOrgId) {
      setFeedback({ kind: 'error', message: 'Pick a source organisation first.' });
      return;
    }
    setFeedback(null);
    copy.mutate(copySourceOrgId, {
      onSuccess: () => {
        setCopyOpen(false);
        setCopySourceOrgId('');
        const srcName =
          (orgs.data ?? []).find((o) => o.id === copySourceOrgId)?.name ?? copySourceOrgId;
        setFeedback({
          kind: 'ok',
          message: `Copied DERM configuration from ${srcName}.`,
        });
      },
      onError: (err) =>
        setFeedback({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to copy DERM config.',
        }),
    });
  }

  // Orgs with DERM configured, excluding this org itself — used to
  // populate the source picker in the "Copy from…" modal.
  const copySourceCandidates = (orgs.data ?? []).filter(
    (o) => o.hasDermConfig && o.id !== orgId && !o.deletedAt,
  );

  function handleRemove() {
    if (!existing) return;
    if (!window.confirm('Remove the DERM integration for this organisation?')) return;
    setFeedback(null);
    remove.mutate(undefined, {
      onSuccess: () => {
        setBaseUrl('');
        setUsername('');
        setPassword('');
        setFeedback({ kind: 'ok', message: 'DERM integration removed.' });
      },
      onError: (err) =>
        setFeedback({
          kind: 'error',
          message: err instanceof Error ? err.message : 'Failed to remove.',
        }),
    });
  }

  return (
    <Card withBorder padding="lg" maw={680}>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={5}>DERM integration</Title>
          {existing ? (
            <Badge color="green" variant="light">
              configured
            </Badge>
          ) : (
            <Badge color="gray" variant="light">
              not configured
            </Badge>
          )}
        </Group>
        <Text size="sm" c="dimmed">
          Credentials used by the platform to submit images to DERM and retrieve results. Username
          and password are encrypted at rest. The password is write-only — leave the field blank to
          keep the existing one.
        </Text>

        {feedback && (
          <Alert
            color={feedback.kind === 'ok' ? 'green' : 'red'}
            icon={
              feedback.kind === 'ok' ? (
                <IconCircleCheck size={18} />
              ) : (
                <IconAlertTriangle size={18} />
              )
            }
          >
            {feedback.message}
          </Alert>
        )}

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Stack gap="md">
            <TextInput
              label="Base URL"
              description="e.g. https://derm.example.com"
              required
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.currentTarget.value)}
              disabled={archived || upsert.isPending}
            />
            <TextInput
              label="Username (login)"
              required
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              disabled={archived || upsert.isPending}
            />
            <PasswordInput
              label="Password"
              description={
                existing
                  ? 'Leave blank to keep the existing password.'
                  : 'Required when configuring DERM for the first time.'
              }
              required={isCreate}
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              disabled={archived || upsert.isPending}
            />
            <Group justify="space-between">
              {existing ? (
                <Button
                  color="red"
                  variant="subtle"
                  type="button"
                  onClick={handleRemove}
                  loading={remove.isPending}
                  disabled={archived || upsert.isPending}
                >
                  Remove
                </Button>
              ) : (
                <span />
              )}
              <Group gap="sm">
                <Button
                  type="button"
                  variant="default"
                  leftSection={<IconCopy size={14} />}
                  onClick={() => {
                    setCopySourceOrgId('');
                    setFeedback(null);
                    setCopyOpen(true);
                  }}
                  disabled={archived || copySourceCandidates.length === 0 || upsert.isPending}
                  title={
                    copySourceCandidates.length === 0
                      ? 'No other organisations with DERM configured.'
                      : undefined
                  }
                >
                  Copy from…
                </Button>
                {existing && (
                  <Button
                    type="button"
                    variant="default"
                    onClick={handleTestConnection}
                    loading={testConn.isPending}
                    disabled={archived || upsert.isPending}
                  >
                    Test connection
                  </Button>
                )}
                <Button type="submit" loading={upsert.isPending} disabled={archived}>
                  {isCreate ? 'Configure DERM' : 'Save changes'}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Box>
      </Stack>

      <Modal
        opened={copyOpen}
        onClose={() => setCopyOpen(false)}
        title="Copy DERM configuration"
        size="md"
      >
        <Stack gap="md">
          <Text size="sm">
            Lift another organisation's DERM credentials into this one. The source's plaintext
            password is decrypted server-side and re-encrypted under this org's own key — it never
            traverses the network.
          </Text>
          {existing && (
            <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
              This will overwrite the existing DERM configuration for this organisation.
            </Alert>
          )}
          <NativeSelect
            label="Source organisation"
            description="Only organisations with DERM already configured are shown."
            value={copySourceOrgId}
            onChange={(e) => setCopySourceOrgId(e.currentTarget.value)}
            data={[
              { value: '', label: 'Select an organisation…' },
              ...copySourceCandidates.map((o) => ({ value: o.id, label: o.name })),
            ]}
            required
          />
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={() => setCopyOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCopy}
              loading={copy.isPending}
              disabled={!copySourceOrgId}
              leftSection={<IconCopy size={14} />}
            >
              Copy
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Card>
  );
}
